import React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, Heart, Stethoscope, TrendingUp, Dna } from 'lucide-react';
import { patientSchema, type PatientFormData } from '@/utils/validation';
import {
    CHDSubgroup,
    PrenatalMethod,
    Outcome30Day,
    MajorMinorFlag,
    ProcedureType,
    getCHDSubgroupLabel,
    getPrenatalMethodLabel,
    getOutcome30DayLabel,
    getMajorMinorFlagLabel,
    getProcedureTypeLabel,
    DataSource,
    getDataSourceLabel,
} from '@/types/patient.types';
import { ICD10Autocomplete } from '@/components/common/ICD10Autocomplete';
import { GeneticConditionAutocomplete } from '@/components/common/GeneticConditionAutocomplete';
import { MalformationCodeAutocomplete } from '@/components/common/MalformationCodeAutocomplete';
import { DiagnosticSection } from './DiagnosticSection';
import { referenceService } from '@/services/referenceService';
import { HospitalOption } from '@/types/reference.types';

// Create a schema specifically for MCC fields to avoid validation errors on hidden fields
const chdSchema = patientSchema.pick({
    case_id: true,
    centre: true,
    subgroup_chd: true,
    icd10_code: true,
    severe_chd: true,
    mca_flag: true,
    syndrome: true,
    genetic_condition_icd10_code: true,
    prenatal_dx: true,
    prenatal_method: true,
    ga_prenatal_dx: true,
    outcome_30d: true,
    reintervention_1y: true,
    procedure_type: true,
    procedure_date: true,
    major_minor_flag: true,
    data_source: true,
    data_quality_note: true,
    other_anomalies_icd10: true,
});

type CHDFormData = z.infer<typeof chdSchema>;

interface PatientCHDFormProps {
    patientId: number;
    defaultValues: Partial<PatientFormData>;
    onSubmit: (data: CHDFormData) => Promise<void>;
    isLoading?: boolean;
    submitLabel?: string;
    isReadOnly?: boolean;
}

export const PatientCHDForm: React.FC<PatientCHDFormProps> = ({
    patientId,
    defaultValues,
    onSubmit,
    isLoading = false,
    submitLabel = "Salvează Datele MCC",
    isReadOnly = false,
}) => {
    const [activeTab, setActiveTab] = React.useState<string>('chd');
    const [hospitals, setHospitals] = React.useState<HospitalOption[]>([]);

    React.useEffect(() => {
        const loadHospitals = async () => {
            const data = await referenceService.getHospitals();
            setHospitals(Array.isArray(data) ? data : []);
        };
        void loadHospitals();
    }, []);

    // Sanitize defaultValues to replace null with undefined for Zod validation
    // For boolean checkbox fields, use false instead of undefined
    const sanitizedDefaultValues = React.useMemo(() => {
        const values = { ...defaultValues };

        // Define all boolean checkbox fields
        const booleanFields: Array<keyof CHDFormData> = [
            'severe_chd',
            'mca_flag',
            'prenatal_dx',
            'reintervention_1y'
        ];

        (Object.keys(values) as Array<keyof typeof values>).forEach((key) => {
            if (values[key] === null) {
                // For boolean fields, use false instead of undefined
                if (booleanFields.includes(key as keyof CHDFormData)) {
                    // @ts-ignore
                    values[key] = false;
                } else {
                    // @ts-ignore
                    values[key] = undefined;
                }
            }
        });

        // Ensure all boolean fields are real JS booleans. The backend returns
        // these as raw MySQL tinyint (0/1, PDO does not cast to bool), which
        // fails z.boolean().optional() with an error that has no <FormMessage>
        // under these fields - the checkbox label turns red and Submit gets
        // disabled with no visible feedback at all.
        booleanFields.forEach((field) => {
            // @ts-ignore
            values[field] = Boolean(values[field as keyof typeof values]);
        });

        return values as CHDFormData;
    }, [defaultValues]);

    const form = useForm<CHDFormData>({
        resolver: zodResolver(chdSchema),
        mode: 'onChange',
        defaultValues: sanitizedDefaultValues,
    });

    const handleSubmit = async (data: CHDFormData) => {
        await onSubmit(data);
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="grid w-full grid-cols-4">
                        <TabsTrigger value="chd" disabled={isReadOnly}>
                            <FileText className="h-4 w-4 mr-1" />
                            Codificare internațională {isReadOnly && '🔒'}
                        </TabsTrigger>
                        <TabsTrigger value="diagnostice" disabled={isReadOnly}>
                            <Heart className="h-4 w-4 mr-1" />
                            Diagnostice {isReadOnly && '🔒'}
                        </TabsTrigger>
                        <TabsTrigger value="prenatal" disabled={isReadOnly}>
                            <Stethoscope className="h-4 w-4 mr-1" />
                            Diagnostic prenatal {isReadOnly && '🔒'}
                        </TabsTrigger>
                        <TabsTrigger value="outcomes" disabled={isReadOnly}>
                            <TrendingUp className="h-4 w-4 mr-1" />
                            Rezultate {isReadOnly && '🔒'}
                        </TabsTrigger>
                    </TabsList>

                    {/* MCC Registry Tab */}
                    <TabsContent value="chd" className="space-y-4 mt-4">
                        <Card>
                            <CardHeader>
                                <CardTitle>Date registrul MCC</CardTitle>
                                <CardDescription>
                                    Informații specifice pentru registry-ul european MCC
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    {/* Centre */}
                                    <FormField
                                        control={form.control}
                                        name="centre"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Centru</FormLabel>
                                                <Select
                                                    onValueChange={(value) => field.onChange(parseInt(value))}
                                                    value={field.value?.toString()}
                                                >
                                                    <FormControl>
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Selectați centrul" />
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent>
                                                        {hospitals.map((hospital) => (
                                                            <SelectItem key={hospital.hospital_id} value={hospital.hospital_id.toString()}>
                                                                {hospital.hospital_name}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    {/* MCC Subgroup */}
                                    <FormField
                                        control={form.control}
                                        name="subgroup_chd"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Subgrup MCC</FormLabel>
                                                <Select
                                                    onValueChange={(value) => field.onChange(parseInt(value))}
                                                    value={field.value?.toString()}
                                                >
                                                    <FormControl>
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Selectați subgrupul" />
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent>
                                                        {Object.values(CHDSubgroup).filter(v => typeof v === 'number').map(subgroup => (
                                                            <SelectItem key={subgroup} value={subgroup.toString()}>
                                                                {getCHDSubgroupLabel(subgroup as CHDSubgroup)}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    {/* ICD-10 Code */}
                                    <FormField
                                        control={form.control}
                                        name="icd10_code"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Cod ICD-10 MCC</FormLabel>
                                                <FormControl>
                                                    <ICD10Autocomplete
                                                        value={field.value ?? ''}
                                                        onChange={(code) => field.onChange(code ?? '')}
                                                        placeholder="Căutați codul ICD-10 pentru malformația cardiacă"
                                                    />
                                                </FormControl>
                                                <FormDescription>
                                                    Cod ICD-10 pentru malformația cardiacă (Q20-Q28)
                                                </FormDescription>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                </div>

                                {/* Checkboxes */}
                                <div className="space-y-4">
                                    <FormField
                                        control={form.control}
                                        name="severe_chd"
                                        render={({ field }) => (
                                            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                                <FormControl>
                                                    <Checkbox
                                                        checked={field.value ?? false}
                                                        onCheckedChange={(checked) => field.onChange(checked === true)}
                                                    />
                                                </FormControl>
                                                <div className="space-y-1 leading-none">
                                                    <FormLabel>
                                                        MCC sever
                                                    </FormLabel>
                                                    <FormDescription>
                                                        Malformație cardiacă congenitală severă conform criteriilor EUROCAT
                                                    </FormDescription>
                                                </div>
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="mca_flag"
                                        render={({ field }) => (
                                            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                                <FormControl>
                                                    <Checkbox
                                                        checked={field.value ?? false}
                                                        onCheckedChange={(checked) => field.onChange(checked === true)}
                                                    />
                                                </FormControl>
                                                <div className="space-y-1 leading-none">
                                                    <FormLabel>
                                                        Anomalii congenitale multiple (MCA)
                                                    </FormLabel>
                                                    <FormDescription>
                                                        Prezintă anomalii congenitale multiple
                                                    </FormDescription>
                                                </div>
                                            </FormItem>
                                        )}
                                    />
                                </div>

                                {/* Other Anomalies ICD-10 */}
                                <FormField
                                    control={form.control}
                                    name="other_anomalies_icd10"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Alte malformații congenitale (cod ICD-10)</FormLabel>
                                            <FormControl>
                                                <MalformationCodeAutocomplete
                                                    value={field.value ?? ''}
                                                    onChange={(value) => field.onChange(value)}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Dna className="h-5 w-5" />
                                    Afecțiune genetică
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <FormField
                                    control={form.control}
                                    name="genetic_condition_icd10_code"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Afecțiune genetică (cod ICD-10)</FormLabel>
                                            <FormControl>
                                                <GeneticConditionAutocomplete
                                                    value={field.value}
                                                    onChange={(code) => field.onChange(code)}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="syndrome"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Altă afecțiune genetică (text)</FormLabel>
                                            <FormControl>
                                                <Input
                                                    placeholder="Descriere afecțiune genetică, dacă nu există cod ICD-10 corespunzător"
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Diagnostice Tab */}
                    <TabsContent value="diagnostice" className="space-y-4 mt-4">
                        {patientId ? (
                            <DiagnosticSection
                                patientId={patientId}
                                isReadOnly={isReadOnly}
                                onDiagnosesChange={(diagnoses) => {
                                    const primary = diagnoses.find((d) => d.is_primary);
                                    form.setValue('icd10_code', primary?.icd10_code || '');
                                }}
                            />
                        ) : (
                            <Card>
                                <CardContent className="py-8">
                                    <div className="text-center text-muted-foreground">
                                        <Heart className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                                        <h3 className="text-lg font-semibold mb-2">Pacient nou</h3>
                                        <p>Salvați mai întâi pacientul pentru a gestiona diagnosticele MCC.</p>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>

                    {/* Prenatal Diagnosis Tab */}
                    <TabsContent value="prenatal" className="space-y-4 mt-4">
                        <Card>
                            <CardHeader>
                                <CardTitle>Diagnostic prenatal</CardTitle>
                                <CardDescription>
                                    Informații despre diagnosticul prenatal al MCC
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {/* Prenatal Diagnosis Checkbox */}
                                <FormField
                                    control={form.control}
                                    name="prenatal_dx"
                                    render={({ field }) => (
                                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                            <FormControl>
                                                <Checkbox
                                                    checked={field.value ?? false}
                                                    onCheckedChange={(checked) => field.onChange(checked === true)}
                                                />
                                            </FormControl>
                                            <div className="space-y-1 leading-none">
                                                <FormLabel>
                                                    Diagnostic prenatal
                                                </FormLabel>
                                                <FormDescription>
                                                    A fost diagnosticat prenatal
                                                </FormDescription>
                                            </div>
                                        </FormItem>
                                    )}
                                />

                                {form.watch('prenatal_dx') && (
                                    <div className="grid grid-cols-2 gap-4 ml-6">
                                        {/* Prenatal Method */}
                                        <FormField
                                            control={form.control}
                                            name="prenatal_method"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Metodă diagnostic prenatal</FormLabel>
                                                    <Select
                                                        onValueChange={(value) => field.onChange(parseInt(value))}
                                                        value={field.value?.toString()}
                                                    >
                                                        <FormControl>
                                                            <SelectTrigger>
                                                                <SelectValue placeholder="Selectați metoda" />
                                                            </SelectTrigger>
                                                        </FormControl>
                                                        <SelectContent>
                                                            {Object.values(PrenatalMethod).filter(v => typeof v === 'number').map(method => (
                                                                <SelectItem key={method} value={method.toString()}>
                                                                    {getPrenatalMethodLabel(method as PrenatalMethod)}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        {/* GA at Prenatal Diagnosis */}
                                        <FormField
                                            control={form.control}
                                            name="ga_prenatal_dx"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Vârstă gestațională la diagnostic (săptămâni)</FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            type="number"
                                                            placeholder="Ex: 20"
                                                            value={field.value ?? ''}
                                                            onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                                                        />
                                                    </FormControl>
                                                    <FormDescription>
                                                        Între 10 și 40 săptămâni
                                                    </FormDescription>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Outcomes Tab */}
                    <TabsContent value="outcomes" className="space-y-4 mt-4">
                        <Card>
                            <CardHeader>
                                <CardTitle>Rezultate</CardTitle>
                                <CardDescription>
                                    Rezultate la 30 zile și follow-up la 1 an
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    {/* Procedure Type */}
                                    <FormField
                                        control={form.control}
                                        name="procedure_type"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Procedură</FormLabel>
                                                <Select
                                                    onValueChange={(value) => field.onChange(parseInt(value))}
                                                    value={field.value?.toString()}
                                                >
                                                    <FormControl>
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Selectați procedura" />
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent>
                                                        {Object.values(ProcedureType).filter(v => typeof v === 'number').map(type => (
                                                            <SelectItem key={type} value={type.toString()}>
                                                                {getProcedureTypeLabel(type as ProcedureType)}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    {/* Procedure Date */}
                                    <FormField
                                        control={form.control}
                                        name="procedure_date"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Data procedurii</FormLabel>
                                                <FormControl>
                                                    <Input type="date" {...field} value={field.value ?? ''} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    {/* 30-Day Outcome */}
                                    <FormField
                                        control={form.control}
                                        name="outcome_30d"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Rezultat la 30 zile</FormLabel>
                                                <Select
                                                    onValueChange={(value) => field.onChange(parseInt(value))}
                                                    value={field.value?.toString()}
                                                >
                                                    <FormControl>
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Selectați rezultatul" />
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent>
                                                        {Object.values(Outcome30Day).filter(v => typeof v === 'number').map(outcome => (
                                                            <SelectItem key={outcome} value={outcome.toString()}>
                                                                {getOutcome30DayLabel(outcome as Outcome30Day)}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    {/* Major/Minor Flag */}
                                    <FormField
                                        control={form.control}
                                        name="major_minor_flag"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Severitate procedură</FormLabel>
                                                <Select
                                                    onValueChange={(value) => field.onChange(parseInt(value))}
                                                    value={field.value?.toString()}
                                                >
                                                    <FormControl>
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Selectați severitatea" />
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent>
                                                        {Object.values(MajorMinorFlag).filter(v => typeof v === 'number').map(flag => (
                                                            <SelectItem key={flag} value={flag.toString()}>
                                                                {getMajorMinorFlagLabel(flag as MajorMinorFlag)}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>

                                {/* Reintervention at 1 Year */}
                                <FormField
                                    control={form.control}
                                    name="reintervention_1y"
                                    render={({ field }) => (
                                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                            <FormControl>
                                                <Checkbox
                                                    checked={field.value ?? false}
                                                    onCheckedChange={(checked) => field.onChange(checked === true)}
                                                />
                                            </FormControl>
                                            <div className="space-y-1 leading-none">
                                                <FormLabel>
                                                    Reintervenție la 1 an
                                                </FormLabel>
                                                <FormDescription>
                                                    A necesitat reintervenție în primul an de viață
                                                </FormDescription>
                                            </div>
                                        </FormItem>
                                    )}
                                />

                                {/* Data Source */}
                                <FormField
                                    control={form.control}
                                    name="data_source"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Sursă date</FormLabel>
                                            <Select
                                                onValueChange={(value) => field.onChange(parseInt(value))}
                                                value={field.value?.toString()}
                                            >
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Selectați sursa" />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    {Object.values(DataSource).filter(v => typeof v === 'number').map(source => (
                                                        <SelectItem key={source} value={source.toString()}>
                                                            {getDataSourceLabel(source as DataSource)}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="data_quality_note"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Notă calitate date</FormLabel>
                                            <FormControl>
                                                <Textarea
                                                    placeholder="Observații despre calitatea datelor"
                                                    className="min-h-[80px]"
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>

                <div className="flex justify-end space-x-2">
                    <Button type="submit" disabled={isLoading || Object.keys(form.formState.errors).length > 0}>
                        {isLoading ? (
                            <>
                                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                Se salvează...
                            </>
                        ) : (
                            submitLabel
                        )}
                    </Button>
                </div>
            </form>
        </Form >
    );
};
